import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { MessageCircle, User } from 'lucide-react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

interface Comment {
  id: string
  author_name: string
  content: string
  created_at: string
}

interface BlogCommentsProps {
  postSlug: string
}

const BlogComments = ({ postSlug }: BlogCommentsProps) => {
  const [comments, setComments] = useState<Comment[]>([])
  const [authorName, setAuthorName] = useState('')
  const [authorEmail, setAuthorEmail] = useState('')
  const [content, setContent] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const { toast } = useToast()

  useEffect(() => {
    fetchComments()
  }, [postSlug])

  const fetchComments = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_comments')
        .select('*')
        .eq('post_slug', postSlug)
        .eq('approved', true)
        .order('created_at', { ascending: false })

      if (error) throw error
      setComments(data || [])
    } catch (error) {
      console.error('Error fetching comments:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!authorName.trim() || !authorEmail.trim() || !content.trim()) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Por favor, preencha todos os campos.',
        variant: 'destructive',
      })
      return
    }

    setIsSubmitting(true)

    try {
      const { error } = await supabase.from('blog_comments').insert({
        post_slug: postSlug,
        author_name: authorName.trim(),
        author_email: authorEmail.trim(),
        content: content.trim(),
      })

      if (error) throw error

      toast({
        title: 'Comentário enviado!',
        description: 'Seu comentário será publicado após aprovação.',
      })

      setAuthorName('')
      setAuthorEmail('')
      setContent('')
    } catch (error) {
      console.error('Error submitting comment:', error)
      toast({
        title: 'Erro ao enviar',
        description:
          'Ocorreu um erro ao enviar seu comentário. Tente novamente.',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="mt-12 space-y-8">
      <div>
        <h2 className="text-3xl font-bold mb-6 flex items-center gap-2">
          <MessageCircle className="h-6 w-6" />
          Comentários ({comments.length})
        </h2>

        {isLoading ? (
          <p className="text-muted-foreground">Carregando comentários...</p>
        ) : comments.length === 0 ? (
          <p className="text-muted-foreground mb-8">
            Seja o primeiro a comentar neste artigo!
          </p>
        ) : (
          <div className="space-y-4 mb-8">
            {comments.map(comment => (
              <Card key={comment.id}>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <CardTitle className="text-base font-semibold">
                      {comment.author_name}
                    </CardTitle>
                    <span className="text-sm text-muted-foreground">•</span>
                    <span className="text-sm text-muted-foreground">
                      {format(
                        new Date(comment.created_at),
                        "d 'de' MMMM, yyyy",
                        {
                          locale: ptBR,
                        },
                      )}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-foreground whitespace-pre-wrap">
                    {comment.content}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Deixe seu comentário</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  placeholder="Seu nome"
                  value={authorName}
                  onChange={e => setAuthorName(e.target.value)}
                  required
                />
              </div>
              <div>
                <Input
                  type="email"
                  placeholder="Seu e-mail"
                  value={authorEmail}
                  onChange={e => setAuthorEmail(e.target.value)}
                  required
                />
              </div>
            </div>
            <div>
              <Textarea
                placeholder="Escreva seu comentário..."
                value={content}
                onChange={e => setContent(e.target.value)}
                rows={4}
                required
              />
            </div>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Enviando...' : 'Enviar Comentário'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

export default BlogComments
